import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaCoins,
  FaFilter,
  FaFileCsv,
  FaPrint,
  FaUser,
  FaCheckCircle,
  FaHistory,
  FaArrowUp,
  FaSearch
} from 'react-icons/fa';

interface LedgerRow {
  sr_no: number;
  member_id: number;
  member_name: string;
  company_name: string;
  date: string;
  activity_type: string;
  activity_title: string;
  details: string;
  reference_id: string;
  points_awarded: number;
  opening_balance: number;
  closing_balance: number;
}

interface MemberLedgerGroup {
  member_id: number;
  member_name: string;
  company_name: string;
  email: string;
  total_activities: number;
  current_closing_points: number;
  ledger: LedgerRow[];
}

export const MemberPointsLedgerReport: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [reportData, setReportData] = useState<{
    summary: {
      total_active_members: number;
      total_activities_recorded: number;
      total_points_distributed: number;
    };
    members: MemberLedgerGroup[];
  }>({
    summary: { total_active_members: 0, total_activities_recorded: 0, total_points_distributed: 0 },
    members: []
  });

  const [selectedMemberId, setSelectedMemberId] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [allMembers, setAllMembers] = useState<any[]>([]);

  const fetchAllMembers = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const active = list.filter((u: any) => u.delete !== 1 && u.role !== 'super-admin');
      active.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setAllMembers(active);
    } catch (err) {
      console.error('Error fetching all members:', err);
    }
  };

  useEffect(() => {
    fetchAllMembers();
  }, []);

  const fetchLedgerReport = async () => {
    setLoading(true);
    try {
      let url = 'https://rmbbackend.rmbjaipur.com/auth/reports/member-points-ledger';
      const params = new URLSearchParams();
      if (selectedMemberId !== 'ALL') params.append('member_id', selectedMemberId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const res = await axios.get(url);
      if (res.data && res.data.success) {
        setReportData({
          summary: res.data.summary,
          members: res.data.members || []
        });
      }
    } catch (err) {
      console.error('Error loading member points ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMembers();
    fetchLedgerReport();
  }, []);

  useEffect(() => {
    fetchLedgerReport();
  }, [selectedMemberId, startDate, endDate]);

  const filteredMembers = reportData.members.filter((m) => {
    const matchesSearch = 
      (m.member_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (m.email || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesMember = selectedMemberId === 'ALL' || String(m.member_id) === String(selectedMemberId);
    return matchesSearch && matchesMember;
  });

  const exportCSV = () => {
    let csv = 'Member Name,Company,Sr No,Date,Activity Type,Activity Title,Details,Reference ID,Points Awarded,Opening Balance,Closing Points Balance\n';
    
    filteredMembers.forEach((m) => {
      m.ledger.forEach((r) => {
        const cleanName = `"${m.member_name.replace(/"/g, '""')}"`;
        const cleanCompany = `"${m.company_name.replace(/"/g, '""')}"`;
        const cleanTitle = `"${r.activity_title.replace(/"/g, '""')}"`;
        const cleanDetails = `"${(r.details || '').replace(/"/g, '""')}"`;
        csv += `${cleanName},${cleanCompany},${r.sr_no},${r.date},${r.activity_type},${cleanTitle},${cleanDetails},${r.reference_id},+${r.points_awarded},${r.opening_balance},${r.closing_balance}\n`;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Member_Points_Activity_Ledger_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCancelTransaction = async (row: LedgerRow) => {
    if (!window.confirm(`Are you sure you want to CANCEL transaction ${row.reference_id} and REVOKE ${row.points_awarded} points from ${row.member_name}?`)) {
      return;
    }

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/ledger/cancel-transaction', {
        member_id: row.member_id,
        reference_id: row.reference_id,
        points_awarded: row.points_awarded
      });

      alert(res.data.message || 'Transaction cancelled and points revoked!');
      fetchLedgerReport();
    } catch (err: any) {
      console.error('Error cancelling transaction:', err);
      alert(err.response?.data?.error || 'Failed to cancel transaction.');
    }
  };

  const [showAdjustModal, setShowAdjustModal] = useState<boolean>(false);
  const [adjustMemberId, setAdjustMemberId] = useState<string>('');
  const [adjustAction, setAdjustAction] = useState<'ADD' | 'DEDUCT' | 'SET'>('ADD');
  const [adjustPoints, setAdjustPoints] = useState<string>('');
  const [adjustReason, setAdjustReason] = useState<string>('');
  const [adjusting, setAdjusting] = useState<boolean>(false);

  const handleAdjustPointsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustMemberId) {
      alert('Please select a member!');
      return;
    }
    if (!adjustPoints || Number(adjustPoints) < 0) {
      alert('Please enter a valid points amount!');
      return;
    }

    setAdjusting(true);
    try {
      const res = await axios.post(`https://rmbbackend.rmbjaipur.com/auth/member/${adjustMemberId}/adjust-points`, {
        points: Number(adjustPoints),
        action: adjustAction,
        reason: adjustReason
      });

      alert(`⭐ ${res.data.message || 'Member points updated successfully!'}`);
      setShowAdjustModal(false);
      setAdjustMemberId('');
      setAdjustPoints('');
      setAdjustReason('');
      fetchLedgerReport();
    } catch (err: any) {
      console.error('Error adjusting points:', err);
      alert(err.response?.data?.error || 'Failed to adjust member points');
    } finally {
      setAdjusting(false);
    }
  };

  const getBadgeColor = (type: string) => {
    switch (type) {
      case 'ATTENDANCE':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
      case 'R2R':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/40';
      case 'REFERRAL':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
      case 'BUSINESS_ACK':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      case 'VISITOR_INVITE':
        return 'bg-teal-500/20 text-teal-300 border-teal-500/40';
      case 'TESTIMONIAL':
        return 'bg-pink-500/20 text-pink-300 border-pink-500/40';
      default:
        return 'bg-slate-700 text-slate-200 border-slate-600';
    }
  };

  return (
    <div className="p-6 bg-[#0F172A] min-h-screen text-slate-100 space-y-6">
      {/* Header Banner */}
      <div className="bg-[#1E2B45] p-6 rounded-2xl border border-blue-500/40 shadow-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-xl font-black text-amber-400 uppercase tracking-wider flex items-center gap-2">
            <FaCoins className="text-amber-400" /> Member Points Activity &amp; Closing Balance Ledger
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Chronological audit report displaying activity details and running closing points balance after each transaction.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowAdjustModal(true)}
            className="px-4 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg flex items-center gap-2 cursor-pointer transition"
          >
            <FaCoins size={14} /> ✏️ Adjust Member Points Manually
          </button>
          <button
            onClick={exportCSV}
            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-lg flex items-center gap-2 cursor-pointer transition"
          >
            <FaFileCsv size={14} /> Export CSV
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-lg flex items-center gap-2 cursor-pointer transition"
          >
            <FaPrint size={14} /> Print PDF
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] shadow-lg flex items-center gap-4">
          <div className="p-3 bg-amber-500/20 rounded-xl text-amber-400">
            <FaUser size={24} />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">Active Members</span>
            <span className="text-2xl font-black text-white">{reportData.summary.total_active_members}</span>
          </div>
        </div>

        <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] shadow-lg flex items-center gap-4">
          <div className="p-3 bg-blue-500/20 rounded-xl text-blue-400">
            <FaHistory size={24} />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">Total Activities Logged</span>
            <span className="text-2xl font-black text-white">{reportData.summary.total_activities_recorded}</span>
          </div>
        </div>

        <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] shadow-lg flex items-center gap-4">
          <div className="p-3 bg-emerald-500/20 rounded-xl text-emerald-400">
            <FaArrowUp size={24} />
          </div>
          <div>
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">Total Distributed Points</span>
            <span className="text-2xl font-black text-emerald-400">{reportData.summary.total_points_distributed} pts</span>
          </div>
        </div>
      </div>

      {/* Filter Controls Bar */}
      <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] shadow-xl space-y-4">
        <div className="flex items-center gap-2 text-amber-300 font-bold text-xs uppercase tracking-wider">
          <FaFilter /> Filter Report Options
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <label className="font-bold text-slate-300 block mb-1">Select Member</label>
            <select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
            >
              <option value="ALL">👥 All Active Members ({reportData.members.length})</option>
              {reportData.members.map((m) => (
                <option key={m.member_id} value={m.member_id}>
                  {m.member_name} ({m.company_name || 'Member'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-bold text-slate-300 block mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
            />
          </div>

          <div>
            <label className="font-bold text-slate-300 block mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
            />
          </div>

          <div>
            <label className="font-bold text-slate-300 block mb-1">Search Name / Company</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search member..."
                className="w-full p-2.5 pl-8 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
              />
              <FaSearch className="absolute left-2.5 top-3 text-slate-400" size={12} />
            </div>
          </div>
        </div>
      </div>

      {/* Member Points Ledger List */}
      {loading ? (
        <div className="bg-[#1E2B45] p-12 rounded-2xl border border-[#2A3B5C] text-center text-slate-400 font-bold">
          ⏳ Generating Member Points Activity &amp; Closing Balance Ledger...
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="bg-[#1E2B45] p-12 rounded-2xl border border-[#2A3B5C] text-center text-slate-400 font-bold">
          No activity records found matching the selected filters.
        </div>
      ) : (
        <div className="space-y-8">
          {filteredMembers.map((m) => (
            <div key={m.member_id} className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl overflow-hidden">
              {/* Member Card Header */}
              <div className="p-4 bg-[#162238] border-b border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-400 font-black flex items-center justify-center border border-amber-500/30">
                    {m.member_name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-black text-white text-sm">{m.member_name}</h3>
                    <p className="text-xs text-slate-400 font-medium">{m.company_name} ({m.email})</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-xs text-slate-400 font-bold">
                    Activities: <strong className="text-white">{m.total_activities}</strong>
                  </span>
                  <span className="px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-xl font-black text-xs flex items-center gap-1.5 shadow">
                    <FaCoins className="text-amber-400" /> Current Closing Points: {m.current_closing_points} pts
                  </span>
                </div>
              </div>

              {/* Activity Points Ledger Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#131E32] text-slate-300 font-bold uppercase border-b border-[#2A3B5C]">
                    <tr>
                      <th className="py-3 px-4 w-12 text-center">#</th>
                      <th className="py-3 px-4 w-28">Date</th>
                      <th className="py-3 px-4 w-36">Category</th>
                      <th className="py-3 px-4">Activity Title &amp; Details</th>
                      <th className="py-3 px-4 w-24">Ref ID</th>
                      <th className="py-3 px-4 w-28 text-right text-emerald-400">Points Added</th>
                      <th className="py-3 px-4 w-32 text-right text-amber-300 font-black">Closing Balance</th>
                      <th className="py-3 px-4 w-28 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2A3B5C]/60 text-slate-200">
                    {m.ledger.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-4 px-4 text-center text-slate-400 font-medium">
                          No activity logs recorded yet for this member (Current Points: <strong className="text-amber-300">{m.current_closing_points} pts</strong>).
                        </td>
                      </tr>
                    ) : (
                      m.ledger.map((r) => (
                        <tr key={`${m.member_id}-${r.sr_no}`} className="hover:bg-[#16243E] transition">
                          <td className="py-3 px-4 text-center font-bold text-slate-400">{r.sr_no}</td>
                          <td className="py-3 px-4 font-mono text-slate-300 font-semibold">{r.date}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border uppercase tracking-wider ${getBadgeColor(r.activity_type)}`}>
                              {r.activity_type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-bold text-white text-xs">{r.activity_title}</div>
                            <div className="text-[11px] text-slate-300 font-medium mt-0.5">{r.details}</div>
                          </td>
                          <td className="py-3 px-4 font-mono text-[11px] text-slate-400">{r.reference_id}</td>
                          <td className="py-3 px-4 text-right font-black text-emerald-400 text-xs">
                            +{r.points_awarded} pts
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="inline-block px-3 py-1 bg-amber-500/20 text-amber-300 font-black text-xs rounded-lg border border-amber-500/40 shadow-sm">
                              {r.closing_balance} pts
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => handleCancelTransaction(r)}
                              className="px-2.5 py-1 bg-red-600/20 hover:bg-red-600 text-red-300 hover:text-white border border-red-500/40 text-[10px] font-bold rounded-lg transition cursor-pointer"
                              title="Cancel transaction and decrease points from member"
                            >
                              ❌ Cancel Points
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* MANUAL MEMBER POINTS ADJUSTMENT MODAL */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black/65 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-md p-6 relative text-slate-100 space-y-4">
            <h3 className="text-base font-black text-amber-400 border-b border-[#2A3B5C] pb-3 flex items-center gap-2 uppercase tracking-wider">
              ⭐ Manual Member Points Adjustment
            </h3>

            <form onSubmit={handleAdjustPointsSubmit} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Select Member *</label>
                <select
                  value={adjustMemberId}
                  onChange={(e) => setAdjustMemberId(e.target.value)}
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                  required
                >
                  <option value="">-- Choose Member ({allMembers.length > 0 ? allMembers.length : reportData.members.length} Members) --</option>
                  {(allMembers.length > 0
                    ? allMembers.map((m: any) => ({
                        id: m.id,
                        name: m.name,
                        company: m.company_name || 'Member',
                        points: m.point || 0
                      }))
                    : reportData.members.map((m) => ({
                        id: m.member_id,
                        name: m.member_name,
                        company: m.company_name,
                        points: m.current_closing_points
                      }))
                  ).map((m: any) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.company} - Current: {m.points} pts)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Action Type *</label>
                <select
                  value={adjustAction}
                  onChange={(e: any) => setAdjustAction(e.target.value)}
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                >
                  <option value="ADD">➕ ADD Points (Credit Bonus Points)</option>
                  <option value="DEDUCT">➖ DEDUCT Points (Penalty / Adjustment)</option>
                  <option value="SET">📌 SET Exact Total Points Balance</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Points Value *</label>
                <input
                  type="number"
                  value={adjustPoints}
                  onChange={(e) => setAdjustPoints(e.target.value)}
                  placeholder="e.g. 10 or 50"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-mono text-sm outline-none font-bold"
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Reason / Admin Note *</label>
                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  rows={3}
                  placeholder="Reason for points adjustment (e.g. Special Event Organizing Bonus, Disciplinary Deduction...)"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-medium outline-none"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="px-4 py-2 bg-[#131E32] hover:bg-slate-800 text-slate-300 font-bold rounded-lg border border-[#2A3B5C]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={adjusting}
                  className="px-5 py-2 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-lg shadow cursor-pointer"
                >
                  {adjusting ? 'Updating Points...' : 'Update Member Points'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
